import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  signal
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {Button} from 'primeng/button';
import {InputTextarea} from 'primeng/inputtextarea';
import {Router} from '@angular/router';
import {firstValueFrom} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';
import {MessageService} from 'primeng/api';
import {UserService} from '../../services/user.service';
import {SendInAppNotificationCommand} from 'admin-api-dto';

type NotificationExample = {
  readonly title: string;
  readonly description: string;
  readonly payload: string;
};

type ExampleDefinition = {
  readonly title: string;
  readonly description: string;
  readonly type: string;
  readonly data: unknown;
};

const EXAMPLE_DEFINITIONS: ExampleDefinition[] = [
  {
    title: 'Auto-apply submitted',
    description: 'Lotteries auto-applied for the user.',
    type: 'auto-apply-submitted',
    data: {lotteries: ['lottery-1', 'lottery-2']},
  },
  {
    title: 'New eligible lotteries',
    description: 'New lotteries the user qualifies for.',
    type: 'new-eligible-lotteries',
    data: {lotteries: ['lottery-1']},
  },
  {
    title: 'New eligible lotteries (auto-apply churned)',
    description: 'Eligible lotteries for churned auto-apply user.',
    type: 'new-eligible-lotteries-auto-apply-churned',
    data: {lotteries: ['lottery-1']},
  },
  {
    title: 'New eligible lotteries (no auto-apply churned)',
    description: 'Eligible lotteries for churned non-auto-apply user.',
    type: 'new-eligible-lotteries-no-auto-apply-churned',
    data: {lotteries: ['lottery-1']},
  },
  {
    title: 'New closed applications',
    description: 'Applications that recently closed.',
    type: 'new-closed-applications',
    data: {lotteries: ['lottery-1']},
  },
  {
    title: 'Lottery number received',
    description: 'A lottery number was assigned.',
    type: 'lottery-number-received',
    data: {lotteries: ['lottery-1']},
  },
  {
    title: 'Not selected',
    description: 'User was not selected in the lottery.',
    type: 'not-selected',
    data: {lotteries: ['lottery-1']},
  },
  {
    title: 'Selected',
    description: 'User was selected in the lottery.',
    type: 'selected',
    data: {lotteries: ['lottery-1']},
  },
  {
    title: 'Message',
    description: 'General in-app message with title and body.',
    type: 'message',
    data: {title: 'Important update', message: 'Check the home screen for the latest steps.'},
  },
  {
    title: 'Subscription canceled',
    description: 'Subscription cancellation notice.',
    type: 'subscription-canceled',
    data: {},
  },
];

@Component({
  selector: 'app-in-app-notification-test-page',
  imports: [
    CommonModule,
    Button,
    InputTextarea
  ],
  templateUrl: './in-app-notification-test-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InAppNotificationTestPageComponent {
  public readonly userId = input.required<string>();

  private static readonly DEFAULT_PAYLOAD = JSON.stringify({
    type: 'message',
    data: {
      title: 'Test notification',
      message: 'This is a test in-app notification from admin.'
    }
  }, null, 2);

  protected readonly examplePayload = InAppNotificationTestPageComponent.DEFAULT_PAYLOAD;
  protected readonly jsonInput = signal(InAppNotificationTestPageComponent.DEFAULT_PAYLOAD);
  protected readonly validationError = signal<string | undefined>(undefined);
  protected readonly loading = signal(false);
  protected readonly examples: NotificationExample[] = EXAMPLE_DEFINITIONS.map(example => ({
    title: example.title,
    description: example.description,
    payload: JSON.stringify({type: example.type, data: example.data}, null, 2),
  }));

  private readonly router = inject(Router);
  private readonly userService = inject(UserService);
  private readonly messageService = inject(MessageService);

  protected resetJson() {
    this.validationError.set(undefined);
    this.jsonInput.set(InAppNotificationTestPageComponent.DEFAULT_PAYLOAD);
  }

  protected goBack() {
    this.router.navigate(['/users', this.userId()]);
  }

  protected async sendTestNotification() {
    this.validationError.set(undefined);
    let parsedPayload: unknown;

    try {
      parsedPayload = JSON.parse(this.jsonInput());
    } catch {
      this.validationError.set('Provided value is not valid JSON.');
      return;
    }

    const command: SendInAppNotificationCommand = parsedPayload
      && typeof parsedPayload === 'object'
      && 'information' in parsedPayload
      ? (parsedPayload as SendInAppNotificationCommand)
      : {
        information: parsedPayload as SendInAppNotificationCommand['information']
      };

    this.loading.set(true);

    try {
      await firstValueFrom(
        this.userService.sendInAppNotification(this.userId(), command)
      );
      this.messageService.add({
        severity: 'success',
        summary: 'In-app notification created',
        detail: 'Test in-app notification created for the user.'
      });
      this.resetJson();
    } catch (error) {
      this.validationError.set(this.getErrorMessage(error));
    } finally {
      this.loading.set(false);
    }
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const serverMessage = this.normalizeServerMessage(error.error);
      if (serverMessage) {
        return serverMessage;
      }
    }

    if (error instanceof Error) {
      return error.message;
    }

    return 'Unable to create in-app notification.';
  }

  private normalizeServerMessage(payload: unknown): string | undefined {
    if (!payload) {
      return undefined;
    }

    if (typeof payload === 'string') {
      return payload;
    }

    if (typeof payload === 'object' && 'message' in payload) {
      const value = (payload as { message?: unknown }).message;
      if (typeof value === 'string') {
        return value;
      }
    }

    return undefined;
  }
}
